import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { aiAssetInsights } from "../../services/ai-proxy.api";
import {
  Button,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Input,
} from "../../components/ui";
import {
  ArrowLeft,
  AlertCircle,
  FileText,
  Calendar,
  Shield,
  MapPin,
  Clock,
  Wrench,
  Sparkles,
  Loader2,
  Upload,
  Trash2,
  Package,
  DollarSign,
  Zap,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  MoreVertical,
  RefreshCw,
  TrendingUp,
  ShoppingBag,
  Receipt,
  History,
} from "lucide-react";
import { AIDrawer } from "../../components/AIDrawer";
import { DIYMaintenanceDrawer } from "../../components/DIYMaintenanceDrawer";

import { SkipReasonModal, SkipReasonValue } from "../../components/maintenance/SkipReasonModal";
import { MaintenanceSection } from "../../components/maintenance/MaintenanceSection";

import { AddWarrantyDrawer } from "../../components/warranty/AddWarrantyDrawer";
import { fileClaimForAsset } from "../../services/warranty.api";
import { getMultipartHeaders } from "../../services/api";
import {
  getAssetById,
  updateAsset,
  deleteAsset,
  lookupProductImage,
  toAbsoluteImageUrl,
  ASSET_LOCATIONS,
  Asset,
} from "../../services/asset.api";
import {
  getAssetSchedule,
  updateScheduledTask,
  generateAssetSchedule,
  ScheduledTask,
} from "../../services/maintenance.api";
import { useProperty } from "../../contexts/PropertyContext";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import {
  getWarrantyStatus,
  calculateHealthScore,
} from "../../utils/healthScore";

// Module-level cache — persists for app session, avoids repeat OpenAI calls
const keyRisksCache = new Map<string, string[]>();

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return "\u2014";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatCurrency(amount?: number | null): string {
  if (amount == null) return "\u2014";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getProductImage(asset: Asset): string {
  return toAbsoluteImageUrl(asset.productImageUrl || asset.imageUrl || "");
}

function getAssetAge(asset: Asset): number {
  const year =
    asset.purchaseYear ||
    (asset.purchasedAt ? new Date(asset.purchasedAt).getFullYear() : null);
  if (!year) return 0;
  return new Date().getFullYear() - year;
}

/** Returns age as a human-readable string, using months when < 1 year */
function formatAssetAge(asset: Asset, t: (key: string, opts?: Record<string, unknown>) => string): { primary: string; secondary: string } {
  const purchaseDate = asset.purchasedAt ? new Date(asset.purchasedAt) : null;
  const year = asset.purchaseYear || purchaseDate?.getFullYear() || null;
  if (!year && !purchaseDate) return { primary: '—', secondary: t('detail.ageLabel.unknown') };

  const now = new Date();
  if (purchaseDate) {
    const totalMonths =
      (now.getFullYear() - purchaseDate.getFullYear()) * 12 +
      (now.getMonth() - purchaseDate.getMonth());
    if (totalMonths < 1) return { primary: '< 1', secondary: t('detail.ageLabel.monthOld') };
    if (totalMonths < 12) return { primary: String(totalMonths), secondary: totalMonths === 1 ? t('detail.ageLabel.monthOld') : t('detail.ageLabel.monthsOld') };
    const years = Math.floor(totalMonths / 12);
    return { primary: String(years), secondary: years === 1 ? t('detail.ageLabel.yearOld') : t('detail.ageLabel.yearsOld') };
  }
  if (!year) return { primary: '—', secondary: t('detail.ageLabel.unknown') };
  const years = now.getFullYear() - year;
  if (years === 0) return { primary: '< 1', secondary: t('detail.ageLabel.yearOld') };
  return { primary: String(years), secondary: years === 1 ? t('detail.ageLabel.yearOld') : t('detail.ageLabel.yearsOld') };
}

function estimateAnnualCost(asset: Asset): number {
  const records = asset.maintenanceRecords ?? [];
  if (records.length === 0) return 0;
  const totalCost = records.reduce((sum, r) => sum + (r.cost ?? 0), 0);
  const age = Math.max(1, getAssetAge(asset));
  return Math.round(totalCost / age);
}

// ── BrandSmart Warranty Card ─────────────────────────────────────────────────

function WarrantyStatusPill({ status }: Readonly<{ status: string }>) {
  const { t } = useTranslation('assets');
  const isActive = status === 'active';
  return (
    <span className={cn(
      "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold",
      isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
    )}>
      <span className={cn("h-1.5 w-1.5 rounded-full", isActive ? "bg-green-500" : "bg-red-500")} />
      {isActive ? t('detail.brandSmartCard.active') : t('detail.brandSmartCard.expired')}
    </span>
  );
}

function BrandSmartWarrantyCard({ asset, formatDate }: Readonly<{ asset: Asset; formatDate: (d?: string | null) => string }>) {
  const { t } = useTranslation('assets');
  const [expandedWarranty, setExpandedWarranty] = React.useState<'mfg' | 'ext' | null>(null);

  const hasMfg = !!asset.manufacturerWarrantyStatus;
  const hasExt = !!(asset.extendedWarrantyStatus && asset.extendedWarrantyStatus !== 'none');
  const bothExist = hasMfg && hasExt;

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b bg-muted/30">
        <ShoppingBag className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('detail.brandSmartCard.purchase')}</span>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Receipt className="h-3.5 w-3.5" />
            <span className="font-mono font-medium text-foreground">{asset.brandSmartReceiptNo}</span>
          </span>
          {asset.purchasedAt && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {formatDate(asset.purchasedAt)}
            </span>
          )}
        </div>
      </div>

      {/* Warranty status cards */}
      <div className={cn("grid gap-0", bothExist ? "grid-cols-2 divide-x" : "grid-cols-1")}>
        {/* Manufacturer warranty */}
        {hasMfg && (
          <div className="flex flex-col">
            <button
              onClick={() => setExpandedWarranty(prev => prev === 'mfg' ? null : 'mfg')}
              className="p-5 flex items-start justify-between gap-3 hover:bg-muted/20 transition-colors text-left w-full"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground font-medium">{t('detail.brandSmartCard.manufacturerWarranty')}</span>
                </div>
                <WarrantyStatusPill status={asset.manufacturerWarrantyStatus ?? ''} />
                {asset.manufacturerWarrantyExpiry && (
                  <p className="text-sm text-muted-foreground">
                    {asset.manufacturerWarrantyStatus === 'expired' ? t('detail.brandSmartCard.expired') : t('detail.brandSmartCard.expires')}{' '}
                    <span className="font-medium text-foreground">{formatDate(asset.manufacturerWarrantyExpiry)}</span>
                  </p>
                )}
              </div>
              <span className={cn("text-muted-foreground mt-1 transition-transform duration-200 text-sm flex-shrink-0", expandedWarranty === 'mfg' && "rotate-180")}>▾</span>
            </button>
            {expandedWarranty === 'mfg' && (
              <div className="px-5 pb-4 border-t bg-muted/10 space-y-2 pt-3">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('detail.brandSmartCard.duration')}</span>
                  <span className="font-medium">{t('detail.brandSmartCard.oneYearStandard')}</span>
                </div>
                {asset.manufacturerWarrantyExpiry && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{t('detail.brandSmartCard.expiryDate')}</span>
                    <span className="font-medium">{formatDate(asset.manufacturerWarrantyExpiry)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('detail.brandSmartCard.providedBy')}</span>
                  <span className="font-medium">{asset.brand || t('detail.brandSmartCard.manufacturerFallback')}</span>
                </div>
                <p className="text-xs text-muted-foreground pt-1 leading-relaxed">
                  {t('detail.brandSmartCard.coversDefects', { brand: asset.brand || t('detail.brandSmartCard.manufacturerGeneric') })}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Extended warranty */}
        {hasExt && (
          <div className="flex flex-col">
            <button
              onClick={() => setExpandedWarranty(prev => prev === 'ext' ? null : 'ext')}
              className="p-5 flex items-start justify-between gap-3 hover:bg-muted/20 transition-colors text-left w-full"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground font-medium">
                    {asset.extendedWarrantyYears ? t('detail.brandSmartCard.extendedWithYears', { years: asset.extendedWarrantyYears }) : t('detail.brandSmartCard.extendedWarranty')}
                  </span>
                </div>
                <WarrantyStatusPill status={asset.extendedWarrantyStatus ?? ''} />
                {asset.extendedWarrantyExpiry && (
                  <p className="text-sm text-muted-foreground">
                    {asset.extendedWarrantyStatus === 'expired' ? t('detail.brandSmartCard.expired') : t('detail.brandSmartCard.expires')}{' '}
                    <span className="font-medium text-foreground">{formatDate(asset.extendedWarrantyExpiry)}</span>
                  </p>
                )}
              </div>
              <span className={cn("text-muted-foreground mt-1 transition-transform duration-200 text-sm flex-shrink-0", expandedWarranty === 'ext' && "rotate-180")}>▾</span>
            </button>
            {expandedWarranty === 'ext' && (
              <div className="px-5 pb-4 border-t bg-muted/10 space-y-2 pt-3">
                {!!asset.extendedWarrantyYears && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{t('detail.brandSmartCard.duration')}</span>
                    <span className="font-medium">{t('detail.brandSmartCard.durationYear', { count: asset.extendedWarrantyYears })}</span>
                  </div>
                )}
                {asset.extendedWarrantyExpiry && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{t('detail.brandSmartCard.expiryDate')}</span>
                    <span className="font-medium">{formatDate(asset.extendedWarrantyExpiry)}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground pt-1 leading-relaxed">
                  {t('detail.brandSmartCard.extendedKicksIn')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delivery address */}
      {asset.deliveryAddress && (
        <div className="flex items-start gap-3 px-5 py-4 border-t">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{t('detail.brandSmartCard.deliveredTo')}</p>
            <p className="text-sm font-medium">{asset.deliveryAddress}</p>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

type WarrantyVariant = 'active' | 'expiring' | 'expired' | 'unknown';
type ProtectionUrgency = 'expired' | 'expiring_soon' | 'active' | 'none';

interface WarrantyBannerInfo {
  bg: string; iconBg: string; iconColor: string; textColor: string; title: string; subText: string; progressBarColor: string;
}

function getProtectionUrgency(warrantyExpiresAt?: string | null): ProtectionUrgency {
  if (!warrantyExpiresAt) return 'none';
  const daysLeft = Math.ceil((new Date(warrantyExpiresAt).getTime() - Date.now()) / 86400000);
  if (daysLeft <= 0) return 'expired';
  if (daysLeft <= 90) return 'expiring_soon';
  return 'active';
}

function getEffectiveWarrantyVariant(warrantyVariant: WarrantyVariant, mfgStatus?: string | null, extStatus?: string | null): WarrantyVariant {
  if (warrantyVariant !== 'unknown') return warrantyVariant;
  if (mfgStatus === 'active' || extStatus === 'active') return 'active';
  if (mfgStatus === 'expired' || extStatus === 'expired') return 'expired';
  return 'unknown';
}

function getWarrantyColor(variant: WarrantyVariant): string {
  if (variant === 'expired') return 'text-red-600';
  if (variant === 'active') return 'text-green-600';
  return 'text-foreground';
}

function getHealthScoreClass(score: number): string {
  if (score >= 8) return 'text-green-600';
  if (score >= 6) return 'text-yellow-600';
  if (score >= 4) return 'text-orange-600';
  return 'text-red-600';
}

function getWarrantyBannerInfo(variant: WarrantyVariant, effectiveExpiryDate: string | null | undefined, warrantyProvider: string | null | undefined): WarrantyBannerInfo {
  let bg = 'bg-muted/30', iconBg = 'bg-muted', iconColor = 'text-muted-foreground', textColor = 'text-foreground', title = 'No Warranty on File';
  if (variant === 'active') {
    bg = 'bg-green-50/50 border-green-200'; iconBg = 'bg-green-100'; iconColor = 'text-green-600'; textColor = 'text-green-700'; title = 'Warranty Active';
  } else if (variant === 'expiring') {
    bg = 'bg-yellow-50/50 border-yellow-200'; iconBg = 'bg-yellow-100'; iconColor = 'text-yellow-600'; textColor = 'text-yellow-700'; title = 'Warranty Expiring Soon';
  } else if (variant === 'expired') {
    bg = 'bg-red-50/50 border-red-200'; iconBg = 'bg-red-100'; iconColor = 'text-red-600'; textColor = 'text-red-700'; title = 'Warranty Expired';
  }
  let subText = 'No expiry date on record';
  if (effectiveExpiryDate) {
    subText = `Covered until ${new Date(effectiveExpiryDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  } else if (warrantyProvider) {
    subText = `Provider: ${warrantyProvider}`;
  }
  let progressBarColor = 'bg-red-500';
  if (variant === 'active') { progressBarColor = 'bg-green-500'; }
  else if (variant === 'expiring') { progressBarColor = 'bg-yellow-500'; }
  return { bg, iconBg, iconColor, textColor, title, subText, progressBarColor };
}

function buildImageLookupOptions(asset: Asset) {
  return {
    assetId: asset.id,
    forceRefresh: false,
    productTitle: asset.productTitle || undefined,
    assetType: asset.subCategory || asset.productCategory || undefined,
    manufacturer: asset.manufacturer || undefined,
    serial: asset.serialNumber || undefined,
  };
}

async function fetchSerpImage(
  asset: Asset,
  setSerpImageUrl: (url: string) => void,
  setIsFetchingImage: (v: boolean) => void,
  forceRefresh = false,
  saveToAsset = true,
): Promise<void> {
  if (!asset.brand && !asset.model) return;
  setIsFetchingImage(true);
  try {
    const result = await lookupProductImage(
      asset.brand || '',
      asset.model || '',
      asset.productCategory || asset.subCategory || asset.category || '',
      { ...buildImageLookupOptions(asset), forceRefresh },
    );
    if (result.imageUrl) {
      setSerpImageUrl(toAbsoluteImageUrl(result.imageUrl));
      // NOTE: DB persistence is handled exclusively by the backend's persistAssetImage()
      // inside the resolve-product-image endpoint (called via lookupProductImage above).
      // The backend guard checks the CURRENT DB value before writing, so it is safe
      // against races. The old frontend updateAsset() call here used a stale closure
      // where asset.productImageUrl was null even after the user had uploaded an image,
      // causing it to overwrite the user's upload. Do not re-add it.
    }
  } catch {
    // Non-fatal
  } finally {
    setIsFetchingImage(false);
  }
}

async function uploadImageAndUpdateAsset(
  file: File,
  asset: Asset,
  setAsset: (a: Asset) => void,
  setImageError: (v: boolean) => void,
  setIsUploadingImage: (v: boolean) => void,
  setUploadedPreview: (v: string) => void,
  fileInputRef: React.RefObject<HTMLInputElement | null>,
): Promise<void> {
  setIsUploadingImage(true);
  try {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', 'Product Image');
    formData.append('type', 'photo');

    // Use getMultipartHeaders() so the tenant slug matches the user's JWT tenant.
    const res = await fetch(
      `${API_BASE}/v1/assets/${encodeURIComponent(asset.id)}/documents/upload`,
      {
        method: 'POST',
        headers: getMultipartHeaders(),
        body: formData,
      },
    );

    if (res.ok) {
      const json = await res.json();
      // assetProductImageUrl is returned by the backend after the DB write — it
      // confirms productImageUrl was actually persisted on the asset row. Using it
      // (rather than doc.url alone) ensures the local state matches the DB value and
      // the image survives navigation away and back.
      const confirmedUrl = (json.data?.assetProductImageUrl ?? json.data?.url) as string | undefined;
      if (confirmedUrl) {
        setAsset({ ...asset, productImageUrl: confirmedUrl });
        setImageError(false);
      } else {
        setUploadedPreview('');
        console.error('Image upload: productImageUrl not confirmed in response', json);
        toast.error('Image uploaded but could not be saved. Please try again.');
      }
    } else {
      const errText = await res.text().catch(() => '');
      console.error('Image upload failed:', res.status, errText);
      setUploadedPreview('');
      toast.error('Image upload failed. Please try again.');
    }
  } catch (err) {
    console.error('Image upload failed:', err);
    setUploadedPreview('');
    toast.error('Image upload failed. Please try again.');
  } finally {
    setIsUploadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }
}

async function uploadDocumentsForAsset(
  files: File[],
  assetId: string,
  fetchAsset: () => Promise<void>,
  setIsUploadingDoc: (v: boolean) => void,
  docInputRef: React.RefObject<HTMLInputElement | null>,
): Promise<void> {
  setIsUploadingDoc(true);
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || '';
  try {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('type', 'other');
      const res = await fetch(`${API_BASE}/v1/assets/${encodeURIComponent(assetId)}/documents/upload`, {
        method: 'POST',
        headers: { Authorization: token ? `Bearer ${token}` : '', 'X-Tenant-Slug': 'brandsmart' },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload failed' }));
        console.error('Doc upload failed:', err.message);
      }
    }
    await fetchAsset();
  } catch (err) {
    console.error('Document upload error:', err);
  } finally {
    setIsUploadingDoc(false);
    if (docInputRef.current) docInputRef.current.value = '';
  }
}

async function fetchAssetById(
  id: string,
  setLoading: (v: boolean) => void,
  setError: (v: string | null) => void,
  setAsset: (a: Asset) => void,
  setImageError: (v: boolean) => void,
  setSerpImageUrl: (v: string | null) => void,
): Promise<void> {
  try {
    setLoading(true);
    setError(null);
    const data = await getAssetById(id);
    setAsset(data);
    setImageError(false);
    setSerpImageUrl(null);
  } catch (err: unknown) {
    setError(err instanceof Error ? err.message : "Failed to load asset");
  } finally {
    setLoading(false);
  }
}

async function fetchAssetSchedule(
  id: string,
  setScheduledTasksLoading: (v: boolean) => void,
  setScheduledTasks: (tasks: ScheduledTask[]) => void,
): Promise<void> {
  try {
    setScheduledTasksLoading(true);
    const tasks = await getAssetSchedule(id);
    setScheduledTasks(tasks);
  } catch {
    // Non-fatal — maintenance tab shows empty state
  } finally {
    setScheduledTasksLoading(false);
  }
}

async function markTaskDone(
  taskId: string, performedAt: string,
  setScheduledTasks: React.Dispatch<React.SetStateAction<ScheduledTask[]>>,
  t: (key: string, opts?: Record<string, unknown>) => string,
): Promise<void> {
  try {
    const updated = await updateScheduledTask(taskId, { status: 'completed', performedAt });
    setScheduledTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    const nextDue = new Date(updated.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    toast.success(t('detail.toast.markedDone', { date: nextDue }));
  } catch {
    toast.error(t('detail.toast.markDoneError'));
  }
}

async function snoozeTask(
  taskId: string, snoozedUntil: string,
  setScheduledTasks: React.Dispatch<React.SetStateAction<ScheduledTask[]>>,
  t: (key: string, opts?: Record<string, unknown>) => string,
): Promise<void> {
  try {
    const updated = await updateScheduledTask(taskId, { status: 'snoozed', snoozedUntil });
    setScheduledTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    const untilDate = new Date(snoozedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    toast.success(t('detail.toast.snoozeUntil', { date: untilDate }));
  } catch {
    toast.error(t('detail.toast.snoozeError'));
  }
}

async function confirmSkipTask(
  skipTaskId: string, reason: SkipReasonValue,
  setScheduledTasks: React.Dispatch<React.SetStateAction<ScheduledTask[]>>,
  setSkipTaskId: (v: string | null) => void,
  t: (key: string, opts?: Record<string, unknown>) => string,
): Promise<void> {
  try {
    const updated = await updateScheduledTask(skipTaskId, { status: 'skipped', skipReason: reason });
    setScheduledTasks((prev) => prev.map((t) => (t.id === skipTaskId ? updated : t)));
    setSkipTaskId(null);
    toast.success(t('detail.toast.skipped'));
  } catch {
    toast.error(t('detail.toast.skipError'));
  }
}

async function deleteAssetAndNavigate(
  assetId: string,
  navigate: (path: string) => void,
  setIsDeleting: (v: boolean) => void,
  setShowDeleteConfirm: (v: boolean) => void,
): Promise<void> {
  setIsDeleting(true);
  try {
    await deleteAsset(assetId);
    navigate("/assets");
  } catch (err) {
    console.error("Delete failed:", err);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
  }
}
export function AssetDetail() { // NOSONAR
  const { t, i18n } = useTranslation('assets');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentProperty } = useProperty();

  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);
  const [isDIYDrawerOpen, setIsDIYDrawerOpen] = useState(false);
  const [showWarrantyDrawer, setShowWarrantyDrawer] = useState(false);
  const [showFileClaimModal, setShowFileClaimModal] = useState(false);
  const [claimForm, setClaimForm] = useState({ title: '', description: '', incidentDate: '' });
  const [claimImages, setClaimImages] = useState<File[]>([]);
  const [claimImagePreviews, setClaimImagePreviews] = useState<string[]>([]);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [aiDrawerMode, setAiDrawerMode] = useState<'parts' | 'booking' | null>(null);
  const [selectedMaintenanceRecord, setSelectedMaintenanceRecord] = useState<{
    taskId?: string;
    title: string;
    description?: string;
    diyStepsJson?: ScheduledTask['diyStepsJson'];
    partsJson?: ScheduledTask['partsJson'];
    difficultyLevel?: string | null;
    estimatedMinutes?: number | null;
    estimatedCost?: string | null;
    diyAvailable?: boolean;
  } | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [serpImageUrl, setSerpImageUrl] = useState<string | null>(null);
  // Local object-URL preview of an image the user just uploaded — shown
  // immediately and given top priority so the user's own image always wins
  // over any SERP-fetched image, and so no lookup is needed to display it.
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);
  const [isFetchingImage, setIsFetchingImage] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);

  // Purchase date states
  const [purchaseYearSaving, setPurchaseYearSaving] = useState(false);
  const [purchaseYearInput, setPurchaseYearInput] = useState('');
  const [purchaseMonthInput, setPurchaseMonthInput] = useState('');
  const [purchaseDayInput, setPurchaseDayInput] = useState('');
  const [purchaseDateSkipped, setPurchaseDateSkipped] = useState(false);
  const [purchaseDateError, setPurchaseDateError] = useState('');
  const [conditionSaving, setConditionSaving] = useState(false);

  // Scheduled maintenance tasks for this asset
  const [scheduledTasks, setScheduledTasks] = useState<ScheduledTask[]>([]);
  const [scheduledTasksLoading, setScheduledTasksLoading] = useState(false);

  // Skip modal state
  const [skipTaskId, setSkipTaskId] = useState<string | null>(null);
  const skipTask = scheduledTasks.find((t) => t.id === skipTaskId);

  // Completed / Skipped section collapse state (independent sections)
  const [completedCollapsed, setCompletedCollapsed] = useState(true);
  const [skippedCollapsed, setSkippedCollapsed] = useState(true);


  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const claimImageInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Set to true after user manually uploads an image — blocks auto-SERP from overwriting it
  const userUploadedImageRef = useRef(false);

  const fetchAsset = useCallback(async () => {
    if (!id) return;
    await fetchAssetById(id, setLoading, setError, setAsset, setImageError, setSerpImageUrl);
  }, [id]);

  const handleWarrantySave = async (updated: Asset) => {
    setAsset(updated);
    setShowWarrantyDrawer(false);
  };

  const fetchScheduledTasks = useCallback(async () => {
    if (!id) return;
    await fetchAssetSchedule(id, setScheduledTasksLoading, setScheduledTasks);
  }, [id]);

  const handleTaskDone = useCallback(async (taskId: string, performedAt: string) => {
    await markTaskDone(taskId, performedAt, setScheduledTasks, t);
  }, []);

  const handleTaskSnooze = useCallback(async (taskId: string, snoozedUntil: string) => {
    await snoozeTask(taskId, snoozedUntil, setScheduledTasks, t);
  }, []);

  const handleSkipConfirm = useCallback(async (reason: SkipReasonValue) => {
    if (!skipTaskId) return;
    await confirmSkipTask(skipTaskId, reason, setScheduledTasks, setSkipTaskId, t);
  }, [skipTaskId]);

  const handleTaskDIY = useCallback((task: ScheduledTask) => {
    setSelectedMaintenanceRecord({
      taskId: task.id,
      title: task.title,
      description: task.description || undefined,
      diyStepsJson: task.diyStepsJson,
      partsJson: task.partsJson,
      difficultyLevel: task.difficultyLevel,
      estimatedMinutes: task.estimatedMinutes,
      estimatedCost: task.estimatedCost,
      diyAvailable: task.diyAvailable,
    });
    setIsDIYDrawerOpen(true);
  }, []);

  const handleTaskOrderParts = useCallback((task: ScheduledTask) => {
    setSelectedMaintenanceRecord({
      taskId: task.id,
      title: task.title,
      description: task.description ?? undefined,
      diyStepsJson: task.diyStepsJson,
      partsJson: task.partsJson,
      difficultyLevel: task.difficultyLevel,
      estimatedMinutes: task.estimatedMinutes,
    });
    setAiDrawerMode('parts');
    setIsAIDrawerOpen(true);
  }, []);

  useEffect(() => {
    fetchAsset();
  }, [fetchAsset]);

  // Only load tasks once the user has set a condition — prevents premature badge count
  useEffect(() => {
    if (asset?.conditionOnAdd) {
      fetchScheduledTasks();
    }
  }, [asset?.conditionOnAdd, fetchScheduledTasks]);

  // Auto-trigger SERP image lookup when asset loads with no image URL at all
  useEffect(() => {
    if (!asset || isFetchingImage || serpImageUrl) return;
    if (userUploadedImageRef.current) return;
    const hasImage = asset.productImageUrl || asset.imageUrl;
    if (!hasImage) {
      fetchSerpImage(asset, setSerpImageUrl, setIsFetchingImage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id]);

  // Revoke the previous object URL when the preview changes or on unmount.
  useEffect(() => {
    return () => {
      if (uploadedPreview) URL.revokeObjectURL(uploadedPreview);
    };
  }, [uploadedPreview]);

  // Reset claim form when modal opens; revoke any old image object URLs on close
  useEffect(() => {
    if (showFileClaimModal) {
      setClaimForm({ title: '', description: '', incidentDate: '' });
      setClaimImages([]);
      setClaimImagePreviews([]);
      setClaimError(null);
    } else {
      setClaimImagePreviews(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return []; });
      setClaimImages([]);
    }
  }, [showFileClaimModal]);

  // Close menu on outside click
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showMenu]);

  const handleImageError = useCallback(async () => {
    if (!asset || isFetchingImage) return;
    // User uploaded their own image in this session — don't replace with SERP
    if (userUploadedImageRef.current) return;
    // Asset already has a persisted productImageUrl (user-uploaded in a prior session).
    // The image failed to load (broken URL, server down) — do NOT replace it with a
    // SERP image. Also do NOT set imageError=true here: that hides the image container
    // entirely, which removes the hover "change image" button the user needs to fix it.
    if (asset.productImageUrl) return;
    setImageError(true);
    await fetchSerpImage(asset, setSerpImageUrl, setIsFetchingImage);
  }, [asset, isFetchingImage]);

  const handleRefreshImage = useCallback(async () => {
    if (!asset || isFetchingImage) return;
    setSerpImageUrl(null);
    setImageError(false);
    toast.info(t('detail.toast.refreshing'));
    await fetchSerpImage(asset, setSerpImageUrl, setIsFetchingImage, true);
  }, [asset, isFetchingImage]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !asset) return;
    // The user explicitly picked their own image. Show it instantly and make
    // sure NO SERP/image-lookup runs for it: set the guard, drop any SERP
    // image, and clear the error state BEFORE the upload so a failed network
    // load of the freshly-uploaded URL can't trigger a lookup.
    userUploadedImageRef.current = true;
    setSerpImageUrl(null);
    setImageError(false);
    setUploadedPreview(URL.createObjectURL(file));
    await uploadImageAndUpdateAsset(file, asset, setAsset, setImageError, setIsUploadingImage, setUploadedPreview, fileInputRef);
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !asset) return;
    await uploadDocumentsForAsset(files, asset.id, fetchAsset, setIsUploadingDoc, docInputRef);
  };

  const handleDelete = async () => {
    if (!asset) return;
    await deleteAssetAndNavigate(asset.id, navigate, setIsDeleting, setShowDeleteConfirm);
  };

  // ── Loading / Error states ──────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
          <AlertCircle className="h-7 w-7 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          {error || t('detail.assetNotFound')}
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate("/assets")}>
          {t('detail.backToAssets')}
        </Button>
      </div>
    );
  }

  // ── Computed values ─────────────────────────────────────────────────────

  const warranty = getWarrantyStatus(asset.warrantyExpiresAt);
  const productImage = getProductImage(asset);
  const assetAge = getAssetAge(asset);
  const healthScore = calculateHealthScore(asset);
  const annualRepairCost = estimateAnnualCost(asset);

  const openIssueCount =
    asset.issues?.filter(
      (i) => i.status === "open" || i.status === "in_progress"
    ).length ?? 0;
  const _totalIssueCount = asset._count?.issues ?? asset.issues?.length ?? 0;
  const maintenanceCount = asset?.conditionOnAdd
    ? scheduledTasks.length
    : 0;
  const documentCount = asset._count?.documents ?? asset.documents?.length ?? 0;

  const warrantyDaysLeft = asset.warrantyExpiresAt
    ? Math.ceil((new Date(asset.warrantyExpiresAt).getTime() - Date.now()) / 86400000)
    : null;
  const protectionUrgency = getProtectionUrgency(asset.warrantyExpiresAt);

  const protectionPlanCopy = {
    expired: {
      badge: t('detail.protection.expired.badge'),
      badgeCls: 'bg-red-50 text-red-700 border-red-200',
      headline: t('detail.protection.expired.headline'),
      body: t('detail.protection.expired.body'),
      cta: t('detail.protection.expired.cta'),
      urgent: true,
    },
    expiring_soon: {
      badge: t('detail.protection.expiringSoon.badge', { days: warrantyDaysLeft }),
      badgeCls: 'bg-orange-50 text-orange-700 border-orange-200',
      headline: t('detail.protection.expiringSoon.headline'),
      body: t('detail.protection.expiringSoon.body'),
      cta: t('detail.protection.expiringSoon.cta'),
      urgent: true,
    },
    active: {
      badge: t('detail.protection.active.badge'),
      badgeCls: 'bg-green-50 text-green-700 border-green-200',
      headline: t('detail.protection.active.headline'),
      body: t('detail.protection.active.body'),
      cta: t('detail.protection.active.cta'),
      urgent: false,
    },
    none: {
      badge: t('detail.protection.none.badge'),
      badgeCls: 'bg-secondary text-muted-foreground border-border',
      headline: t('detail.protection.none.headline'),
      body: t('detail.protection.none.body'),
      cta: t('detail.protection.none.cta'),
      urgent: false,
    },
  }[protectionUrgency];

  const effectiveWarrantyVariant = getEffectiveWarrantyVariant(warranty.variant, asset.manufacturerWarrantyStatus, asset.extendedWarrantyStatus);

  const effectiveExpiryDate =
    asset.warrantyExpiresAt ||
    (asset.manufacturerWarrantyStatus === 'active' ? asset.manufacturerWarrantyExpiry : null) ||
    (asset.extendedWarrantyStatus === 'active' ? asset.extendedWarrantyExpiry : null);

  const warrantyColor = getWarrantyColor(effectiveWarrantyVariant);
  const showUpgradeCTA = effectiveWarrantyVariant === "expired" || assetAge > 8;
  const healthScoreClass = getHealthScoreClass(healthScore);
  const upgradeCopyText = (() => {
    if (warranty.variant === 'expired') return t('detail.upgrade.expiredCopy');
    if (assetAge > 8) return t('detail.upgrade.oldCopy', { age: assetAge });
    return t('detail.upgrade.defaultCopy', { category: asset.category.toLowerCase() });
  })();
  const { bg: warrantyBannerBg, iconBg: warrantyBannerIconBg, iconColor: warrantyBannerIconColor, textColor: warrantyBannerTextColor, progressBarColor: warrantyProgressBarColor } = getWarrantyBannerInfo(effectiveWarrantyVariant, effectiveExpiryDate, asset.warrantyProvider);
  const warrantyBannerTitle = effectiveWarrantyVariant === 'active' ? t('detail.warrantyBanner.active') : effectiveWarrantyVariant === 'expiring' ? t('detail.warrantyBanner.expiring') : effectiveWarrantyVariant === 'expired' ? t('detail.warrantyBanner.expired') : t('detail.warrantyBanner.noFile');
  const warrantySubText = effectiveExpiryDate ? t('detail.warrantyBanner.coveredUntil', { date: new Date(effectiveExpiryDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) }) : asset.warrantyProvider ? t('detail.warrantyBanner.provider', { name: asset.warrantyProvider }) : t('detail.warrantyBanner.noExpiry');


  // -- Tab render helpers ------------------------------------------------

  const renderOverviewTab = () => (
          <TabsContent value="overview">
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Specifications */}
              <div className="rounded-xl border bg-card p-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4 flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5" />
                  {t('detail.section.specifications')}
                </h3>
                <div>
                  {[
                    { label: "Brand", value: asset.brand },
                    { label: "Model", value: asset.model },
                    { label: "Serial No.", value: asset.serialNumber },
                    {
                      label: "Category",
                      value: asset.subCategory || asset.category,
                    },
                    { label: "Color", value: asset.productColor },
                    { label: "Barcode", value: asset.barcode },
                    { label: "Manufacturer", value: asset.manufacturer },
                  ]
                    .filter((r) => r.value)
                    .map((row) => (
                      <div
                        key={row.label}
                        className="flex items-center justify-between py-2.5 border-b last:border-b-0"
                      >
                        <span className="text-xs text-muted-foreground">
                          {row.label}
                        </span>
                        <span className="text-xs font-medium text-right max-w-[55%] truncate">
                          {row.value}
                        </span>
                      </div>
                    ))}
                  {![
                    asset.brand,
                    asset.model,
                    asset.serialNumber,
                    asset.productColor,
                    asset.barcode,
                    asset.manufacturer,
                  ].some(Boolean) && (
                    <p className="text-sm text-muted-foreground py-2">
                      No specifications on file.
                    </p>
                  )}
                </div>
              </div>

              {/* Purchase & Value */}
              <div className="rounded-xl border bg-card p-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4 flex items-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5" />
                  Purchase & Value
                </h3>
                <div>
                  {[
                    {
                      label: "Purchased",
                      value: asset.purchasedAt
                        ? formatDate(asset.purchasedAt)
                        : String(asset.purchaseYear ?? '—'),
                      show: !!(asset.purchaseYear || asset.purchasedAt),
                    },
                    {
                      label: "Location",
                      value: asset.location,
                      show: !!asset.location,
                    },
                    {
                      label: "Purchase Price",
                      value: formatCurrency(asset.purchasePrice),
                      show: asset.purchasePrice != null,
                    },
                    {
                      label: "Current Value",
                      value: formatCurrency(asset.currentValue),
                      show: asset.currentValue != null,
                    },
                    {
                      label: "Depreciation",
                      value:
                        asset.purchasePrice != null &&
                        asset.currentValue != null
                          ? formatCurrency(
                              asset.purchasePrice - asset.currentValue
                            )
                          : null,
                      show:
                        asset.purchasePrice != null &&
                        asset.currentValue != null,
                    },
                  ]
                    .filter((r) => r.show)
                    .map((row) => (
                      <div
                        key={row.label}
                        className="flex items-center justify-between py-2.5 border-b last:border-b-0"
                      >
                        <span className="text-xs text-muted-foreground">
                          {row.label}
                        </span>
                        <span className="text-xs font-medium">{row.value}</span>
                      </div>
                    ))}
                  {!asset.purchasePrice &&
                    !asset.currentValue &&
                    !asset.purchaseYear &&
                    !asset.purchasedAt &&
                    !asset.location && (
                      <p className="text-sm text-muted-foreground py-2">
                        No purchase info on file.
                      </p>
                    )}
                </div>
                {asset.energyUsageKwh != null && (
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Zap className="h-3 w-3" />
                      Energy usage
                    </span>
                    <span className="text-xs font-medium">
                      {asset.energyUsageKwh} kWh/yr
                      <span className="text-muted-foreground font-normal ml-1">
                        (~{formatCurrency(asset.energyUsageKwh * 0.12)}/yr)
                      </span>
                    </span>
                  </div>
                )}
                {/* Missing fields — one chip per field, inline edit */}
                <InlineFieldEditor asset={asset} onUpdated={(updated) => setAsset(updated)} />
              </div>

              {asset.productDescription && (
                <div className="rounded-xl border bg-card p-5 sm:col-span-2">
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Description
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {asset.productDescription}
                  </p>
                </div>
              )}

              {asset.notes && (
                <div className="rounded-xl border bg-card p-5 sm:col-span-2">
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Notes
                  </h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {asset.notes}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
  );

  const renderWarrantyTab = () => (
          <TabsContent value="warranty">
            <div className="space-y-4">

              {/* ── Status banner ─────────────────────────────────────────── */}
              <div className={cn(
                "rounded-xl border p-5 flex items-center gap-5",
                warrantyBannerBg
              )}>
                <div className={cn(
                  "h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0",
                  warrantyBannerIconBg
                )}>
                  <Shield className={cn("h-6 w-6", warrantyBannerIconColor)} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn("text-lg font-semibold", warrantyBannerTextColor)}>
                    {warrantyBannerTitle}
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {warrantySubText}
                  </p>
                </div>
                {warranty.variant !== 'unknown' && warranty.daysLeft != null && warranty.daysLeft > 0 && (
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-bold tabular-nums">{warranty.daysLeft}</p>
                    <p className="text-xs text-muted-foreground">{t('detail.warrantyDet.daysLeft')}</p>
                  </div>
                )}
                {/* Edit warranty button (shown when warranty info exists) */}
                {(asset.manufacturerWarrantyExpiry || asset.extendedWarrantyExpiry || asset.warrantyExpiresAt) && (
                  <button
                    onClick={() => setShowWarrantyDrawer(true)}
                    className="ml-auto flex-shrink-0 text-xs text-blue-600 hover:underline font-medium"
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* ── No warranty CTA ───────────────────────────────────────── */}
              {effectiveWarrantyVariant === 'unknown' && !asset.brandSmartReceiptNo && (
                <div className="rounded-xl border-2 border-dashed border-border bg-muted/10 p-6 flex flex-col items-center gap-3 text-center">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                    <Shield className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t('detail.warrantyDet.noInfoTitle')}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Do you have a warranty for this asset? Add it here so HomeIQ can track expiry and send reminders.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowWarrantyDrawer(true)}
                    className="mt-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                  >
                    + Add Warranty Info
                  </button>
                </div>
              )}

              {/* ── BrandSmart warranty card ───────────────────────────────── */}
              {asset.brandSmartReceiptNo && (
                <BrandSmartWarrantyCard asset={asset} formatDate={formatDate} />
              )}

              {/* ── Standard warranty details ─────────────────────────────── */}
              {(asset.warrantyExpiresAt || asset.warrantyProvider || asset.warrantyNumber || asset.warrantyType) && (
                <div className="rounded-xl border bg-card p-5">
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    Warranty Details
                  </h3>
                  <div>
                    {[
                      { label: "Expires", value: formatDate(asset.warrantyExpiresAt), show: !!asset.warrantyExpiresAt },
                      { label: "Provider", value: asset.warrantyProvider, show: !!asset.warrantyProvider },
                      { label: "Policy #", value: asset.warrantyNumber, show: !!asset.warrantyNumber },
                      { label: "Type", value: asset.warrantyType, show: !!asset.warrantyType },
                      { label: "Deductible", value: formatCurrency(asset.deductible), show: asset.deductible != null },
                      { label: "Max Claim", value: formatCurrency(asset.maxClaimAmount), show: asset.maxClaimAmount != null },
                      { label: "Claims Used", value: String(asset.claimsUsed ?? 0), show: asset.claimsUsed != null },
                    ].filter(r => r.show).map(row => (
                      <div key={row.label} className="flex items-center justify-between py-2.5 border-b last:border-b-0">
                        <span className="text-xs text-muted-foreground">{row.label}</span>
                        <span className="text-xs font-medium">{row.value}</span>
                      </div>
                    ))}
                  </div>
                  {asset.warrantyExpiresAt && warranty.variant !== 'unknown' && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div
                          className={cn("h-full rounded-full transition-all", warrantyProgressBarColor)}
                          style={{ width: `${Math.max(4, Math.min(100, warranty.variant === 'expired' ? 100 : 100 - (warranty.daysLeft / 730) * 100))}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">{warranty.label}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── Coverage & Exclusions ─────────────────────────────────── */}
              {((asset.warrantyCoverage && asset.warrantyCoverage.length > 0) ||
                (asset.warrantyExclusions && asset.warrantyExclusions.length > 0)) && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {asset.warrantyCoverage && asset.warrantyCoverage.length > 0 && (
                    <div className="rounded-xl border bg-card p-5">
                      <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                        Covered
                      </h3>
                      <ul className="space-y-2">
                        {asset.warrantyCoverage.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {asset.warrantyExclusions && asset.warrantyExclusions.length > 0 && (
                    <div className="rounded-xl border bg-card p-5">
                      <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <XCircle className="h-3 w-3 text-red-500" />
                        Excluded
                      </h3>
                      <ul className="space-y-2">
                        {asset.warrantyExclusions.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <XCircle className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* ── Active protection plans ───────────────────────────────── */}
              {asset.protectionPlans && asset.protectionPlans.length > 0 && (
                <div className="rounded-xl border bg-card p-5">
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4 flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Protection Plans
                  </h3>
                  <div>
                    {asset.protectionPlans.map((plan) => (
                      <div key={plan.id} className="flex items-start justify-between gap-4 py-3 border-b last:border-b-0">
                        <div>
                          <p className="text-sm font-medium">{plan.planName}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{plan.provider} &middot; {plan.priceLabel}</p>
                        </div>
                        <span className={cn(
                          "text-[10px] px-2 py-0.5 rounded-full border font-medium flex-shrink-0",
                          plan.isActive ? "bg-green-50 text-green-700 border-green-200" : "bg-secondary text-muted-foreground border-border"
                        )}>
                          {plan.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── File a Claim CTA ──────────────────────────────────────── */}
              {(asset.protectionPlans?.some(p => p.isActive) || asset.warrantyExpiresAt || asset.manufacturerWarrantyStatus === 'active' || asset.extendedWarrantyStatus === 'active' || asset.brandSmartReceiptNo) && (
                <div className="rounded-xl border bg-card p-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Need a replacement or repair?</p>
                      <p className="text-xs text-muted-foreground mt-0.5">File a warranty claim for this asset</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setClaimForm({ title: '', description: '', incidentDate: '' }); setClaimError(null); setShowFileClaimModal(true); }}
                    className="flex-shrink-0 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    File a Claim
                  </button>
                </div>
              )}

              {/* ── Registered under ──────────────────────────────────────── */}
              {asset.warrantyRegisteredEmail && (
                <div className="rounded-xl border bg-card p-5 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('detail.section.warrantyRegisteredUnder')}</p>
                    <p className="text-sm font-medium text-foreground">
                      {asset.warrantyRegisteredName
                        ? `${asset.warrantyRegisteredName} (${asset.warrantyRegisteredEmail})`
                        : asset.warrantyRegisteredEmail}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowWarrantyDrawer(true)}
                    className="ml-auto text-xs text-blue-600 hover:underline font-medium flex-shrink-0"
                  >
                    Edit
                  </button>
                </div>
              )}

            </div>
          </TabsContent>
  );

  const handleConditionSelect = async (conditionValue: string) => {
    if (!asset) return;
    setConditionSaving(true);
    try {
      await generateAssetSchedule(asset.id, conditionValue);
      setAsset((prev) => prev ? { ...prev, conditionOnAdd: conditionValue } : prev);
      await fetchScheduledTasks();
    } catch {
      toast.error(t('detail.maintenance.conditionSaveError'));
      setConditionSaving(false);
    }
  };

  const renderMaintenanceTab = () => {
    const step1Done = !!(asset?.purchasedAt || asset?.purchaseYear || purchaseDateSkipped);
    return (
          <TabsContent value="maintenance">
            <div className="space-y-4">
              {/* ── Setup steps ─────────────────────────────────── */}
              {asset && !asset.conditionOnAdd && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Step 1 box */}
                    <div className={cn(
                      'rounded-xl border bg-white transition-all duration-300',
                      step1Done
                        ? 'border-green-200 bg-green-50/40'
                        : 'border-zinc-200 shadow-sm',
                    )}>
                      {/* Step label */}
                      <div className={cn(
                        'flex items-center gap-2 px-4 pt-3 pb-1',
                      )}>
                        <span className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition-colors duration-300',
                          step1Done ? 'bg-green-500 text-white' : 'bg-blue-500 text-white',
                        )}>
                          {step1Done ? '✓' : '1'}
                        </span>
                        <span className={cn(
                          'text-xs font-semibold uppercase tracking-wider transition-colors duration-300',
                          step1Done ? 'text-green-700' : 'text-zinc-500',
                        )}>
                          {t('detail.maintenance.step1')}
                        </span>
                      </div>

                      <div className="px-4 pb-3">
                        {step1Done ? (
                          /* Completed state */
                          <div className="flex items-center gap-2 text-sm text-green-700 py-1 animate-in fade-in duration-300">
                            <CheckCircle2 className="h-4 w-4 shrink-0" />
                            <span className="font-medium">
                              {purchaseDateSkipped
                                ? t('detail.maintenance.skipped')
                                : (asset.purchasedAt && new Date(asset.purchasedAt).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })) || t('detail.maintenance.yearOnly', { year: asset.purchaseYear })}
                            </span>
                          </div>
                        ) : (
                          /* Active form */
                          <div className="animate-in fade-in duration-200">
                            <p className="text-sm text-zinc-500 mb-2.5">{t('detail.maintenance.step1Question')} <span className="text-zinc-400">{t('detail.maintenance.step1Hint')}</span></p>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Input
                                type="number"
                                placeholder={t('detail.maintenance.yearPlaceholder')}
                                value={purchaseYearInput}
                                onChange={(e) => { setPurchaseYearInput(e.target.value); setPurchaseDateError(''); }}
                                min="1900"
                                max={new Date().getFullYear().toString()}
                                className="text-zinc-900 w-20 h-8 text-xs"
                              />
                              <select
                                value={purchaseMonthInput}
                                onChange={(e) => { setPurchaseMonthInput(e.target.value); setPurchaseDateError(''); }}
                                className="h-8 rounded-md border border-zinc-200 text-xs text-zinc-700 px-2 bg-white w-28 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="">{t('detail.maintenance.monthPlaceholder')}</option>
                                {Array.from({ length: 12 }, (_, i) => (
                                  <option key={i + 1} value={i + 1}>
                                    {new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(new Date(2024, i, 1))}
                                  </option>
                                ))}
                              </select>
                              <Input
                                type="number"
                                placeholder={t('detail.maintenance.dayPlaceholder')}
                                value={purchaseDayInput}
                                onChange={(e) => { setPurchaseDayInput(e.target.value); setPurchaseDateError(''); }}
                                min="1"
                                max="31"
                                className="text-zinc-900 w-14 h-8 text-xs"
                              />
                              <Button
                                onClick={async () => {
                                  setPurchaseDateError('');
                                  const year = Number.parseInt(purchaseYearInput, 10);
                                  const currentYear = new Date().getFullYear();
                                  if (!purchaseYearInput || Number.isNaN(year)) { setPurchaseDateError(t('detail.maintenance.yearRequired')); return; }
                                  if (year < 1900 || year > currentYear) { setPurchaseDateError(t('detail.maintenance.yearRange', { max: currentYear })); return; }
                                  const month = purchaseMonthInput ? Number.parseInt(purchaseMonthInput, 10) : null;
                                  const day = purchaseDayInput ? Number.parseInt(purchaseDayInput, 10) : null;
                                  if (day !== null) {
                                    if (Number.isNaN(day) || day < 1 || day > 31) { setPurchaseDateError(t('detail.maintenance.dayRange')); return; }
                                    if (month !== null) {
                                      const maxDay = new Date(year, month, 0).getDate();
                                      if (day > maxDay) { setPurchaseDateError(t('detail.maintenance.monthDayError', { month: new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(new Date(year, month - 1, 1)), year, max: maxDay })); return; }
                                    }
                                  }
                                  setPurchaseYearSaving(true);
                                  try {
                                    let purchasedAt: string | undefined;
                                    if (month) {
                                      purchasedAt = new Date(year, month - 1, day ?? 15).toISOString();
                                      await updateAsset(asset.id, { purchasedAt, purchaseYear: year });
                                    } else {
                                      await updateAsset(asset.id, { purchaseYear: year });
                                    }
                                    setAsset((prev) => prev ? { ...prev, purchasedAt: purchasedAt ?? prev.purchasedAt, purchaseYear: year } : prev);
                                    toast.success(t('detail.maintenance.saveSuccess'));
                                    setPurchaseYearSaving(false);
                                  } catch {
                                    toast.error(t('detail.maintenance.saveError'));
                                    setPurchaseYearSaving(false);
                                  }
                                }}
                                disabled={purchaseYearSaving || !purchaseYearInput}
                                size="sm"
                                className="h-8 text-xs px-3 shrink-0"
                              >
                                {purchaseYearSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('detail.maintenance.save')}
                              </Button>
                              <button
                                onClick={() => setPurchaseDateSkipped(true)}
                                className="text-xs text-zinc-400 hover:text-zinc-500 transition-colors px-1"
                              >
                                {t('detail.maintenance.skip')}
                              </button>
                            </div>
                            {purchaseDateError && (
                              <p className="text-[11px] text-red-500 mt-2 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3 shrink-0" />
                                {purchaseDateError}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Step 2 box — condition */}
                    <div className={cn(
                      'rounded-xl border bg-white transition-all duration-500',
                      !step1Done && 'opacity-40 pointer-events-none select-none',
                      step1Done ? 'border-blue-200 shadow-sm animate-in fade-in zoom-in-95 duration-300' : 'border-zinc-200',
                    )}>
                      {/* Step label */}
                      <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                        <span className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition-colors duration-300',
                          step1Done ? 'bg-blue-500 text-white' : 'bg-zinc-300 text-white',
                        )}>
                          2
                        </span>
                        <span className={cn(
                          'text-xs font-semibold uppercase tracking-wider',
                          step1Done ? 'text-zinc-500' : 'text-zinc-400',
                        )}>
                          {t('detail.maintenance.step2')}
                        </span>
                      </div>

                      <div className="px-4 pb-3">
                        <p className="text-sm text-zinc-500 mb-2.5">{t('detail.maintenance.step2Question')} <span className="text-zinc-400">{t('detail.maintenance.step2Hint')}</span></p>
                        {conditionSaving ? (
                          <div className="flex items-center gap-2 text-sm text-green-700 animate-in fade-in duration-200">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>{t('detail.maintenance.generatingSchedule')}</span>
                          </div>
                        ) : (
                          <div className="flex gap-2 flex-wrap animate-in fade-in duration-200">
                            {[
                              { value: 'new',     cls: 'border-blue-400 hover:bg-blue-50 text-blue-700' },
                              { value: 'good',    cls: 'border-green-400 hover:bg-green-50 text-green-700' },
                              { value: 'fair',    cls: 'border-amber-400 hover:bg-amber-50 text-amber-700' },
                              { value: 'poor',    cls: 'border-red-400 hover:bg-red-50 text-red-700' },
                              { value: 'unknown', cls: 'border-zinc-300 hover:bg-zinc-50 text-zinc-600' },
                            ].map((c) => (
                              <button
                                key={c.value}
                                onClick={() => handleConditionSelect(c.value)}
                                className={cn(
                                  'px-4 py-2 text-xs font-semibold rounded-lg border-2 bg-white transition-all duration-150 active:scale-95',
                                  c.cls,
                                )}
                              >
                                {t(`detail.maintenance.condition.${c.value}`)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
              )}

              {/* Scheduled Tasks Section — only shown after condition is set */}
              {asset?.conditionOnAdd && scheduledTasksLoading && (
                <div className="rounded-xl border bg-card p-8 flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">{t('detail.maintenance.loadingSchedule')}</span>
                </div>
              )}
              {asset?.conditionOnAdd && !scheduledTasksLoading && scheduledTasks.length > 0 && (() => {
                const overdue = scheduledTasks.filter((t) => t.status === 'overdue');
                const upcoming = scheduledTasks.filter((t) => t.status === 'upcoming');
                const snoozed = scheduledTasks.filter((t) => t.status === 'snoozed');
                const completed = scheduledTasks.filter((t) => t.status === 'completed');
                const skipped = scheduledTasks.filter((t) => t.status === 'skipped');
                return (
                  <div className="space-y-2">
                    <MaintenanceSection
                      title={t('detail.maintenance.section.overdue')}
                      icon={AlertCircle}
                      iconColor="text-red-600 dark:text-red-400"
                      headerBg="bg-red-50 dark:bg-red-950/20"
                      count={overdue.length}
                      tasks={overdue}
                      onDone={handleTaskDone}
                      onSkip={setSkipTaskId}
                      onSnooze={handleTaskSnooze}
                      onDIY={handleTaskDIY}
                      onOrderParts={handleTaskOrderParts}
                    />
                    <MaintenanceSection
                      title={t('detail.maintenance.section.schedule')}
                      icon={CheckCircle2}
                      iconColor="text-blue-600 dark:text-blue-400"
                      headerBg="bg-blue-50 dark:bg-blue-950/20"
                      count={upcoming.length}
                      tasks={upcoming}
                      onDone={handleTaskDone}
                      onSkip={setSkipTaskId}
                      onSnooze={handleTaskSnooze}
                      onDIY={handleTaskDIY}
                      onOrderParts={handleTaskOrderParts}
                    />
                    <MaintenanceSection
                      title={t('detail.maintenance.section.snoozed')}
                      icon={History}
                      iconColor="text-zinc-500 dark:text-zinc-400"
                      headerBg="bg-zinc-50 dark:bg-zinc-900"
                      count={snoozed.length}
                      tasks={snoozed}
                      onDone={handleTaskDone}
                      onSkip={setSkipTaskId}
                      onSnooze={handleTaskSnooze}
                      onDIY={handleTaskDIY}
                      onOrderParts={handleTaskOrderParts}
                    />
                    {completed.length > 0 && (
                      <MaintenanceSection
                        title={t('detail.maintenance.section.completed')}
                        icon={CheckCircle2}
                        iconColor="text-green-600 dark:text-green-400"
                        headerBg="bg-green-50 dark:bg-green-950/20"
                        count={completed.length}
                        tasks={completed}
                        collapsed={completedCollapsed}
                        onToggle={() => setCompletedCollapsed((v) => !v)} // NOSONAR
                        onDone={handleTaskDone}
                        onSkip={setSkipTaskId}
                        onSnooze={handleTaskSnooze}
                        onDIY={handleTaskDIY}
                        onOrderParts={handleTaskOrderParts}
                      />
                    )}
                    {skipped.length > 0 && (
                      <MaintenanceSection
                        title={t('detail.maintenance.section.skipped')}
                        icon={XCircle}
                        iconColor="text-amber-600 dark:text-amber-400"
                        headerBg="bg-amber-50 dark:bg-amber-950/20"
                        count={skipped.length}
                        tasks={skipped}
                        collapsed={skippedCollapsed}
                        onToggle={() => setSkippedCollapsed((v) => !v)} // NOSONAR
                        onDone={handleTaskDone}
                        onSkip={setSkipTaskId}
                        onSnooze={handleTaskSnooze}
                        onDIY={handleTaskDIY}
                        onOrderParts={handleTaskOrderParts}
                      />
                    )}
                  </div>
                );
              })()}
              {/* No tasks yet — show placeholder only if condition was already set */}
              {!scheduledTasksLoading && scheduledTasks.length === 0 && asset?.conditionOnAdd && (
                <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
                  {t('detail.maintenance.noTasks')}
                </div>
              )}

            </div>
          </TabsContent>
    );
  };

  const renderIssuesTab = () => (
          <TabsContent value="issues">
            {asset.issues && asset.issues.length > 0 ? (
              <div className="space-y-3">
                {asset.issues
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  .map((issue) => {
                    const isOpen =
                      issue.status === "open" ||
                      issue.status === "in_progress";
                    let issueSeverityCls = 'bg-secondary text-muted-foreground border-border';
                    if (issue.severity === 'critical') { issueSeverityCls = 'bg-red-50 text-red-700 border-red-200'; }
                    else if (issue.severity === 'high') { issueSeverityCls = 'bg-red-50 text-red-600 border-red-200'; }
                    return (
                      <div
                        key={issue.id}
                        className="rounded-xl border bg-card p-4 flex items-start gap-4"
                      >
                        <div
                          className={cn(
                            "h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5",
                            isOpen ? "bg-red-50" : "bg-green-50"
                          )}
                        >
                          {isOpen ? (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium">{issue.title}</p>
                            <span
                              className={cn(
                                "text-[10px] px-2 py-0.5 rounded-full border font-medium flex-shrink-0 capitalize",
                                issueSeverityCls
                              )}
                            >
                              {issue.severity}
                            </span>
                          </div>
                          {issue.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {issue.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(issue.createdAt)}
                            </span>
                            <span className="text-xs text-muted-foreground capitalize">
                              {issue.status.replace("_", " ")}
                            </span>
                            {issue.resolvedAt && (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                {t('detail.issues.resolved', { date: formatDate(issue.resolvedAt) })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="rounded-xl border bg-card p-12 text-center">
                <div className="h-12 w-12 rounded-2xl bg-secondary mx-auto mb-4 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-base font-medium">{t('detail.issues.noneTitle')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('detail.issues.noneDesc')}
                </p>
              </div>
            )}
          </TabsContent>
  );

  const renderDocumentsTab = () => (
          <TabsContent value="documents">
            {/* Upload button — always visible */}
            <div className="flex justify-end mb-4">
              <input
                ref={docInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                multiple
                className="hidden"
                onChange={handleDocumentUpload}
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={isUploadingDoc}
                onClick={() => docInputRef.current?.click()}
              >
                {isUploadingDoc ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                {isUploadingDoc ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>

            {asset.documents && asset.documents.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {asset.documents.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl border bg-card p-4 flex items-start gap-3 hover:bg-secondary/30 transition-colors cursor-pointer"
                  >
                    <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                        {doc.type}
                        {doc.sizeBytes > 0 &&
                          ` \u00B7 ${(doc.sizeBytes / 1024).toFixed(0)} KB`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(doc.createdAt)}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <button
                type="button"
                className="rounded-xl border-2 border-dashed bg-card p-12 text-center cursor-pointer hover:bg-secondary/20 transition-colors w-full"
                onClick={() => docInputRef.current?.click()}
              >
                <div className="h-12 w-12 rounded-2xl bg-secondary mx-auto mb-4 flex items-center justify-center">
                  <Upload className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-base font-medium">{t('detail.documents.noneTitle')}</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">
                  Click to upload receipts, manuals, warranty cards, or invoices.
                </p>
              </button>
            )}
          </TabsContent>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6">
        {/* Hero Section */}
        <section className="mb-8">
          {/* Main Info Card */}
          <div className="rounded-2xl border bg-card overflow-hidden mb-6">
            <div className="p-6">
              {/* Back + three-dot row */}
              <div className="flex items-center justify-between mb-5">
                <button
                  onClick={() => navigate("/assets")}
                  className="h-8 w-8 rounded-lg border flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setShowMenu((v) => !v)}
                    className="h-8 w-8 rounded-lg border flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 mt-2 w-48 rounded-xl border bg-white shadow-xl z-50 overflow-hidden">
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setClaimForm({ title: '', description: '', incidentDate: '' });
                          setClaimError(null);
                          setShowFileClaimModal(true);
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                      >
                        <Shield className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                        File a Claim
                      </button>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          navigate('/claims');
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                      >
                        <FileText className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                        My Claims
                      </button>
                      <div className="border-t border-gray-100 mx-3" />
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          handleRefreshImage();
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                      >
                        <RefreshCw className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                        Refresh image
                      </button>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setShowDeleteConfirm(true);
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                      >
                        <Trash2 className="h-3.5 w-3.5 flex-shrink-0" />
                        Delete asset
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Product Image */}
                <div className="flex-shrink-0">
                  <div className="w-48 h-48 lg:w-56 lg:h-56 rounded-xl border bg-background flex items-center justify-center overflow-hidden relative group">
                    {isUploadingImage && (
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    )}
                    {!isUploadingImage && isFetchingImage && (
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span className="text-xs">Finding image…</span>
                      </div>
                    )}
                    {!isUploadingImage && !isFetchingImage && (!imageError || serpImageUrl || uploadedPreview) && (productImage || serpImageUrl || uploadedPreview) && (
                      <>
                        <img
                          src={uploadedPreview || serpImageUrl || productImage}
                          alt={asset.name}
                          referrerPolicy="no-referrer"
                          className="w-full h-full object-contain p-4"
                          onError={(uploadedPreview || serpImageUrl) ? undefined : () => handleImageError()}
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-all rounded-xl"
                          title={t('detail.changeImage')}
                        >
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-2">
                            <Upload className="h-6 w-6 text-white" />
                            <span className="text-xs text-white font-medium">{t('detail.changeImage')}</span>
                          </div>
                        </button>
                      </>
                    )}
                    {!isUploadingImage && !isFetchingImage && !(!imageError || serpImageUrl || uploadedPreview) && !(productImage || serpImageUrl || uploadedPreview) && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex flex-col items-center gap-3 w-full h-full p-4 hover:bg-accent/50 transition-colors rounded-xl"
                        title={t('detail.inline.uploadImage')}
                      >
                        <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center flex-shrink-0">
                          <Package className="h-8 w-8 text-muted-foreground opacity-60" />
                        </div>
                        <div className="text-center">
                          {asset.brand && <p className="text-sm font-semibold">{asset.brand}</p>}
                          {asset.model && <p className="text-xs text-muted-foreground">{asset.model}</p>}
                          <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1 justify-center">
                            <Upload className="h-3 w-3" /> Upload image
                          </p>
                        </div>
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </div>

                {/* Info & Metrics */}
                <div className="flex-1 min-w-0 space-y-6">
                  {/* Header */}
                  <div>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <h2 className="text-3xl lg:text-4xl font-light tracking-tight mb-2">
                          {asset.name}
                        </h2>
                        {(asset.brand || asset.model) && (
                          <p className="text-base text-muted-foreground">
                            {[asset.brand, asset.model].filter(Boolean).join(" • ")}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {asset.subCategory || asset.category}
                      </Badge>
                    </div>
                  </div>

                  {/* Key Metrics Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Asset Age */}
                    {(() => {
                      const ageLabel = formatAssetAge(asset, t);
                      return (
                        <div className="text-center p-4 rounded-lg border bg-background">
                          <div className="flex items-center justify-center gap-1.5 mb-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {t('detail.metric.age')}
                            </p>
                          </div>
                          <p className="text-3xl font-semibold tabular-nums mb-1">
                            {ageLabel.primary}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {ageLabel.secondary}
                          </p>
                        </div>
                      );
                    })()}

                    {/* Warranty Status */}
                    <div className="text-center p-4 rounded-lg border bg-background">
                      <div className="flex items-center justify-center gap-1.5 mb-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('detail.metric.warranty')}
                        </p>
                      </div>
                      <p className={cn("text-lg font-semibold mb-1", warrantyColor)}>
                        {effectiveWarrantyVariant === 'active' ? t('warranty.active') : effectiveWarrantyVariant === 'expiring' ? t('warranty.expiring') : effectiveWarrantyVariant === 'expired' ? t('warranty.expired') : t('warranty.none')}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {effectiveExpiryDate
                          ? t('detail.metric.expires', { date: new Date(effectiveExpiryDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) })
                          : warranty.label || t('detail.metric.noCoverage')}
                      </p>
                    </div>

                    {/* Open Issues */}
                    <div className="text-center p-4 rounded-lg border bg-background">
                      <div className="flex items-center justify-center gap-1.5 mb-2">
                        <AlertCircle className="h-4 w-4 text-muted-foreground" />
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('detail.metric.issues')}
                        </p>
                      </div>
                      <p className={cn(
                        "text-3xl font-semibold tabular-nums mb-1",
                        openIssueCount > 0 ? "text-red-600" : "text-foreground"
                      )}>
                        {openIssueCount}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {openIssueCount === 0 ? t('detail.metric.allClear') : t('detail.metric.open')}
                      </p>
                    </div>

                    {/* Health Score */}
                    <div className="text-center p-4 rounded-lg border bg-background">
                      <div className="flex items-center justify-center gap-1.5 mb-2">
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('detail.metric.health')}
                        </p>
                      </div>
                      <p className={cn(
                        "text-3xl font-semibold tabular-nums mb-1",
                        healthScoreClass
                      )}>
                        {healthScore}<span className="text-base font-normal text-muted-foreground">/10</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {healthScore >= 8 ? t('detail.health.excellent') : healthScore >= 6 ? t('detail.health.good') : healthScore >= 4 ? t('detail.health.fair') : t('detail.health.poor')}
                      </p>
                    </div>
                  </div>

                  {/* Quick Details */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
                    {asset.location && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {t('detail.quick.location')}
                        </p>
                        <p className="text-sm font-medium">{asset.location}</p>
                      </div>
                    )}
                    {assetAge > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {t('detail.metric.age')}
                        </p>
                        <p className="text-sm font-medium">
                          {assetAge} {t('detail.quick.year', { count: assetAge })}
                        </p>
                      </div>
                    )}
                    {asset.serialNumber && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('detail.quick.serialNumber')}</p>
                        <p className="text-sm font-medium font-mono truncate">
                          {asset.serialNumber}
                        </p>
                      </div>
                    )}
                    {(asset.purchasedAt || asset.purchaseYear) && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                          <ShoppingBag className="h-3 w-3" />
                          {t('detail.quick.purchased')}
                        </p>
                        <p className="text-sm font-medium">
                          {asset.purchasedAt
                            ? new Date(asset.purchasedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                            : (asset.purchaseYear && String(asset.purchaseYear)) || formatDate(asset.purchasedAt)}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {t('detail.quick.added')}
                      </p>
                      <p className="text-sm font-medium">{formatDate(asset.createdAt)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Cards Row */}
          <div className={cn("grid gap-4", showUpgradeCTA ? "lg:grid-cols-3" : "lg:grid-cols-2")}>
            {/* Upgrade Opportunity */}
            {showUpgradeCTA && (
              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="h-10 w-10 rounded-lg border flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold mb-1">
                      {t('detail.upgrade.title')}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {upgradeCopyText}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mb-4">
                  {asset.energyUsageKwh && asset.energyUsageKwh > 400 && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Zap className="h-3.5 w-3.5" />
                      Save ${Math.round(asset.energyUsageKwh * 0.12 * 0.3)}/yr
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Shield className="h-3.5 w-3.5" />
                    {t('detail.upgrade.fullWarranty')}
                  </span>
                  {annualRepairCost > 100 && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Wrench className="h-3.5 w-3.5" />
                      ~{formatCurrency(annualRepairCost)}/yr repairs
                    </span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate(`/assets/${asset.id}/upgrade`)}
                >
                  {t('detail.upgrade.explore')}
                </Button>
              </div>
            )}

            {/* Browse Protection Plans — always shown, urgency-aware */}
            <div className={cn(
              "rounded-xl border bg-card p-6",
              protectionPlanCopy.urgent && "border-orange-200 bg-orange-50/30 dark:bg-orange-950/10"
            )}>
              <div className="flex items-start gap-4 mb-4">
                <div className={cn(
                  "h-10 w-10 rounded-lg border flex items-center justify-center flex-shrink-0",
                  protectionPlanCopy.urgent ? "border-orange-300 bg-orange-50" : ""
                )}>
                  <Shield className={cn("h-5 w-5", protectionPlanCopy.urgent ? "text-orange-600" : "")} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-base font-semibold">
                      {t('detail.protection.title')}
                    </h3>
                    <span className={cn("text-[10px] font-medium px-2 py-0.5 rounded-full border", protectionPlanCopy.badgeCls)}>
                      {protectionPlanCopy.badge}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {protectionPlanCopy.body}
                  </p>
                </div>
              </div>
              <Button
                variant={protectionPlanCopy.urgent ? "default" : "outline"}
                size="sm"
                className="w-full gap-2"
                onClick={() =>
                  navigate(
                    `/warranty-plans?assetId=${asset.id}&assetName=${encodeURIComponent(
                      asset.name
                    )}&category=${encodeURIComponent(asset.category)}`
                  )
                }
              >
                <Shield className="h-4 w-4" />
                {protectionPlanCopy.cta}
              </Button>
            </div>

            {/* Key Risks — AI summary card */}
            <div className="rounded-xl border bg-card p-6">
              <InlineRiskSummary asset={asset} isExpired={effectiveWarrantyVariant === 'expired'} standalone />
            </div>
          </div>

        </section>

        {/* ── Tabs ───────────────────────────────────────────────────────── */}
        <Tabs defaultValue="overview">
          <TabsList className="w-full justify-start bg-transparent border-b rounded-none h-auto p-0 gap-0 mb-6">
            {[
              { value: "overview", label: t('detail.tab.overview') },
              { value: "warranty", label: t('detail.tab.warranty') },
              {
                value: "maintenance",
                label: t('detail.tab.maintenance'),
                count: maintenanceCount > 0 ? maintenanceCount : undefined,
              },
              {
                value: "issues",
                label: t('detail.tab.issues'),
                count: openIssueCount > 0 ? openIssueCount : undefined,
              },
              {
                value: "documents",
                label: t('detail.tab.documents'),
                count: documentCount > 0 ? documentCount : undefined,
              },
            ].map(({ value, label, count }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm gap-1.5 whitespace-nowrap"
              >
                {label}
                {count != null && (
                  <span className="ml-1 h-4 min-w-4 px-1 rounded-full bg-muted text-muted-foreground text-[10px] flex items-center justify-center font-medium">
                    {count}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* __ OVERVIEW _____________________________________________________ */}
          {renderOverviewTab()}

          {/* __ WARRANTY _____________________________________________________ */}
          {renderWarrantyTab()}

          {/* __ MAINTENANCE __________________________________________________ */}
          {renderMaintenanceTab()}

          {/* __ ISSUES _______________________________________________________ */}
          {renderIssuesTab()}

          {/* __ DOCUMENTS ____________________________________________________ */}
          {renderDocumentsTab()}
        </Tabs>
      </div>

      {/* ── Delete confirmation ──────────────────────────────────────────── */}
      {showDeleteConfirm && (
        <>
          <button
            type="button"
            aria-label={t('detail.aria.closeDialog')}
            className="fixed inset-0 bg-black/50 z-50 cursor-default"
            onClick={() => !isDeleting && setShowDeleteConfirm(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm p-6 rounded-2xl bg-background border shadow-xl">
            <h3 className="text-base font-medium mb-1.5">{t('detail.delete.title')}</h3>
            <p className="text-sm text-muted-foreground mb-5">
              <strong>{asset.name}</strong> will be permanently deleted. This
              cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
                className="gap-2"
              >
                {isDeleting && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Delete
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ── Floating AI Diagnosis button ─────────────────────────────────── */}
      <div
        className="fixed bottom-6 right-6 z-40 flex items-center gap-3"
        style={{ animation: 'aiEnter 0.45s cubic-bezier(0.34,1.56,0.64,1) both' }}
      >
        {/* Label */}
        <div className="flex flex-col items-end select-none">
          <span className="text-[9px] font-semibold uppercase tracking-widest leading-none mb-1 text-muted-foreground">
            Powered by AI
          </span>
          <span className="text-[13px] font-bold text-foreground leading-none">
            AI Diagnosis
          </span>
        </div>

        {/* Round dark FAB */}
        <button
          onClick={() => setIsAIDrawerOpen(true)}
          aria-label={t('detail.aria.aiDiagnosis')}
          className="group relative h-14 w-14 rounded-full focus:outline-none hover:scale-110 active:scale-95 transition-transform duration-200 flex-shrink-0"
          style={{ boxShadow: '0 6px 24px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.2)' }}
        >
          {/* Pulse ring */}
          <span className="absolute -inset-2 rounded-full animate-ping [animation-duration:2.5s] opacity-30"
            style={{ background: 'radial-gradient(circle, rgba(15,23,42,0.6) 0%, transparent 70%)' }} />
          {/* Dark filled circle */}
          <span className="absolute inset-0 rounded-full transition-all duration-200 group-hover:brightness-110"
            style={{ background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 60%, #020617 100%)' }} />
          {/* Thin gloss top edge */}
          <span className="absolute inset-0 rounded-full bg-gradient-to-b from-white/12 to-transparent pointer-events-none" />
          {/* Inner border */}
          <span className="absolute inset-0 rounded-full ring-1 ring-inset ring-white/10" />
          {/* Sparkles icon */}
          <span className="absolute inset-0 flex items-center justify-center">
            <Sparkles
              className="h-5 w-5 text-white relative z-10"
              style={{ animation: 'aiIconPulse 2.5s ease-in-out infinite' }}
            />
          </span>
        </button>
      </div>

      <style>{`
        @keyframes aiEnter {
          from { opacity: 0; transform: translateY(12px) scale(0.88); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes aiIconPulse {
          0%, 100% { transform: scale(1)    rotate(0deg);  opacity: 1;   }
          50%       { transform: scale(1.2)  rotate(14deg); opacity: 0.85; }
        }
      `}</style>

      {isAIDrawerOpen && (
        <AIDrawer
          assetId={asset.id}
          homeId={currentProperty?.id || ''}
          assetName={asset.name}
          assetBrand={asset.brand || asset.manufacturer}
          assetModel={asset.model}
          assetCategory={asset.subCategory || asset.category}
          directMode={aiDrawerMode}
          maintenanceTask={selectedMaintenanceRecord?.title}
          preloadedTaskParts={selectedMaintenanceRecord?.partsJson}
          onClose={() => {
            setIsAIDrawerOpen(false);
            setSelectedMaintenanceRecord(null);
            setAiDrawerMode(null);
          }}
        />
      )}

      {/* ── Skip Reason Modal ────────────────────────────────────────────── */}
      {skipTask && (
        <SkipReasonModal
          taskTitle={skipTask.title}
          onConfirm={handleSkipConfirm}
          onClose={() => setSkipTaskId(null)}
        />
      )}



      {isDIYDrawerOpen && selectedMaintenanceRecord && (
        <DIYMaintenanceDrawer
          maintenanceTitle={selectedMaintenanceRecord.title}
          assetName={asset.name}
          assetLocation={asset.location || null}
          assetBrand={asset.brand || undefined}
          assetModel={asset.model || undefined}
          preloadedSteps={selectedMaintenanceRecord.diyStepsJson}
          preloadedParts={selectedMaintenanceRecord.partsJson}
          preloadedDifficulty={selectedMaintenanceRecord.difficultyLevel}
          preloadedDurationMinutes={selectedMaintenanceRecord.estimatedMinutes}
          estimatedCost={selectedMaintenanceRecord.estimatedCost ?? null}
          diyAvailable={selectedMaintenanceRecord.diyAvailable ?? true}
          onClose={() => {
            setIsDIYDrawerOpen(false);
            setSelectedMaintenanceRecord(null);
          }}
          onComplete={() => {
            // Refresh asset data to show updated maintenance status
            fetchAsset();
          }}
          onBookTechnician={() => {
            // Open AI drawer in technician-only mode
            setIsDIYDrawerOpen(false);
            setAiDrawerMode('booking');
            setIsAIDrawerOpen(true);
          }}
          _onOrderParts={() => {
            // Open AI drawer in parts-only mode
            setIsDIYDrawerOpen(false);
            setAiDrawerMode('parts');
            setIsAIDrawerOpen(true);
          }}
        />
      )}

      {/* ── Warranty drawer ─────────────────────────────────────────────── */}
      <AddWarrantyDrawer
        open={showWarrantyDrawer}
        onClose={() => setShowWarrantyDrawer(false)}
        asset={asset}
        onSaved={handleWarrantySave}
      />

      {/* ── File Claim Modal ─────────────────────────────────────────────── */}
      {showFileClaimModal && (
        <div className="fixed inset-x-0 bottom-0 top-16 z-40 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-full">

            {/* ── Header ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">File a Warranty Claim</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{asset.name}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowFileClaimModal(false)}
                className="h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors text-base"
              >
                ✕
              </button>
            </div>

            {/* ── Scrollable Fields ── */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              {/* Claim Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Claim Title</label>
                <input
                  value={claimForm.title}
                  onChange={e => setClaimForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Device stopped working"
                  required
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 focus:bg-white transition-colors"
                />
              </div>

              {/* Problem Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Problem Description</label>
                <textarea
                  value={claimForm.description}
                  onChange={e => setClaimForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe what happened, when it started, any symptoms or error messages…"
                  rows={3}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 focus:bg-white transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">Be specific — the more detail, the faster your claim is processed</p>
              </div>

              {/* Upload Photos */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Upload Photos&nbsp;<span className="text-gray-400 font-normal text-xs">(optional · max 5)</span>
                </label>
                <input
                  ref={claimImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    const combined = [...claimImages, ...files].slice(0, 5);
                    setClaimImages(combined);
                    setClaimImagePreviews(combined.map(f => URL.createObjectURL(f)));
                    if (claimImageInputRef.current) claimImageInputRef.current.value = '';
                  }}
                />
                {claimImages.length < 5 ? (
                  <button
                    type="button"
                    onClick={() => claimImageInputRef.current?.click()}
                    className="w-full rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-6 flex flex-col items-center gap-2 hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 cursor-pointer group"
                  >
                    <div className="h-10 w-10 rounded-full bg-gray-100 group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                      <Upload className="h-5 w-5 text-gray-400 group-hover:text-primary transition-colors" />
                    </div>
                    <span className="text-sm font-medium text-gray-500 group-hover:text-gray-700">Click to upload photos</span>
                    <span className="text-xs text-gray-400">{claimImages.length}/5 photos added</span>
                  </button>
                ) : (
                  <div className="w-full rounded-xl border-2 border-dashed border-gray-100 bg-gray-50 py-3 flex items-center justify-center">
                    <span className="text-xs text-gray-400">Maximum 5 photos reached</span>
                  </div>
                )}
                {claimImagePreviews.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {claimImagePreviews.map((url, idx) => (
                      <div key={url} className="relative group/img">
                        <img src={url} alt={`claim-${idx}`} className="h-[72px] w-[72px] object-cover rounded-xl border border-gray-200 shadow-sm" />
                        <button
                          type="button"
                          onClick={() => {
                            URL.revokeObjectURL(url);
                            const imgs = claimImages.filter((_, i) => i !== idx);
                            setClaimImages(imgs);
                            setClaimImagePreviews(imgs.map((f) => URL.createObjectURL(f)));
                          }}
                          className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px] leading-none hover:bg-red-600 shadow-sm opacity-0 group-hover/img:opacity-100 transition-opacity"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Incident Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Incident Date</label>
                <input
                  type="date"
                  value={claimForm.incidentDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={e => setClaimForm(f => ({ ...f, incidentDate: e.target.value }))}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 focus:bg-white transition-colors"
                />
                <p className="text-xs text-gray-400 mt-1">When did the issue first occur?</p>
              </div>

              {claimError && (
                <div className="flex items-start gap-2.5 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{claimError}</p>
                </div>
              )}
            </div>

            {/* ── Fixed Footer ── */}
            <div className="flex gap-3 px-6 py-4 border-t bg-gray-50/80 flex-shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setShowFileClaimModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={claimSubmitting}
                onClick={async () => {
                  if (!claimForm.title.trim()) { setClaimError('Title is required.'); return; }
                  if (!claimForm.description.trim()) { setClaimError('Problem description is required.'); return; }
                  if (!claimForm.incidentDate) { setClaimError('Incident date is required.'); return; }
                  const homeId = currentProperty?.id ?? '';
                  if (!homeId) { setClaimError('No home selected. Please select a home first.'); return; }
                  setClaimError(null);
                  setClaimSubmitting(true);
                  try {
                    await fileClaimForAsset({
                      homeId,
                      assetId: asset.id,
                      title: claimForm.title.trim(),
                      description: claimForm.description.trim() || undefined,
                      incidentDate: new Date(claimForm.incidentDate).toISOString(),
                    });
                    setShowFileClaimModal(false);
                    toast.success('Claim submitted successfully! We\'ll review it shortly.');
                  } catch (err: unknown) {
                    setClaimError(err instanceof Error ? err.message : 'Failed to submit claim.');
                  } finally {
                    setClaimSubmitting(false);
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm"
              >
                {claimSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {claimSubmitting ? 'Submitting…' : 'Submit Claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline Field Editor — one chip per missing field ────────────────────────

type EditableField = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select';
  placeholder?: string;
  options?: readonly string[];
  missing: (a: Asset) => boolean;
  toPayload: (val: string) => Partial<Asset>;
};

const EDITABLE_FIELDS: EditableField[] = [
  {
    key: 'location',
    label: 'Room / Location',
    type: 'select',
    options: ASSET_LOCATIONS,
    missing: (a) => !a.location,
    toPayload: (v) => ({ location: v }),
  },
  {
    key: 'purchasedAt',
    label: 'Purchase Date',
    type: 'date',
    missing: (a) => !a.purchasedAt && !a.purchaseYear,
    toPayload: (v) => ({ purchasedAt: v ? new Date(v).toISOString() : undefined }),
  },
  {
    key: 'purchasePrice',
    label: 'Purchase Price',
    type: 'number',
    placeholder: 'e.g. 1299',
    missing: (a) => a.purchasePrice == null,
    toPayload: (v) => ({ purchasePrice: Number.parseFloat(v) }),
  },
];

function InlineFieldEditor({ asset, onUpdated }: Readonly<{ asset: Asset; onUpdated: (a: Asset) => void }>) {
  const { t } = useTranslation('assets');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const missingFields = EDITABLE_FIELDS.filter((f) => f.missing(asset));
  if (missingFields.length === 0) return null;

  const activeField = EDITABLE_FIELDS.find((f) => f.key === activeKey) ?? null;

  function open(field: EditableField) {
    setActiveKey(field.key);
    setValue('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function cancel() {
    setActiveKey(null);
    setValue('');
  }

  async function save() {
    if (!activeField || !value.trim()) return;
    setSaving(true);
    try {
      const updated = await updateAsset(asset.id, activeField.toPayload(value.trim()));
      onUpdated(updated);
      setActiveKey(null);
      setValue('');
    } catch {
      // keep form open so user can retry
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 pt-4 border-t">
      {activeField ? (
        <div>
          <p className="text-xs text-muted-foreground mb-2">Add {activeField.label}</p>
          <div className="flex gap-2">
            {activeField.type === 'select' ? (
              <select
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="flex-1 text-xs border rounded-md px-3 py-2 bg-background text-zinc-900 focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">Select a room...</option>
                {activeField.options?.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                ref={inputRef}
                type={activeField.type}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={activeField.placeholder}
                onKeyDown={(e) => { if (e.key === 'Enter') { save(); } else if (e.key === 'Escape') { cancel(); } }}
                className="flex-1 text-xs border rounded-md px-3 py-2 bg-background text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            )}
            <button
              onClick={save}
              disabled={saving || !value.trim()}
              className="text-xs px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
            >
              {saving ? '...' : 'Save'}
            </button>
            <button
              onClick={cancel}
              className="text-xs px-3 py-2 rounded-md border text-muted-foreground hover:bg-muted"
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">{t('detail.spec.completeRecord')}</p>
          <div className="flex flex-wrap gap-2">
            {missingFields.map((f) => (
              <button
                key={f.key}
                onClick={() => open(f)}
                className="text-xs px-2.5 py-1 rounded-full border border-dashed border-primary/40 text-primary/70 hover:border-primary hover:text-primary transition-colors"
              >
                + {f.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Key Risks Section (AI-powered, module-level cached) ──────────────────────

function InlineRiskSummary({ asset, isExpired, standalone }: Readonly<{ asset: Asset; isExpired: boolean; standalone?: boolean }>) {
  const { t } = useTranslation('assets');
  const cacheKey = `${asset.brand}-${asset.model}-${asset.category}-${isExpired ? 'exp' : 'act'}`;
  const [summary, setSummary] = useState<string | null>(() => {
    const cached = keyRisksCache.get(cacheKey);
    return cached ? cached.join(' ') : null;
  });
  const [loading, setLoading] = useState(!keyRisksCache.has(cacheKey));

  useEffect(() => {
    if (keyRisksCache.has(cacheKey)) {
      const cached = keyRisksCache.get(cacheKey) ?? [];
      setSummary(cached.join(' '));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const age = asset.purchaseYear ? new Date().getFullYear() - asset.purchaseYear : undefined;
    aiAssetInsights({
      assetName: asset.name,
      brand: asset.brand || undefined,
      model: asset.model || undefined,
      category: asset.category,
      subCategory: asset.subCategory || undefined,
      ageYears: age,
    })
      .then((data) => {
        if (cancelled) return;
        const risks = data.keyRisks.map(r => `${r.title}: ${r.description}`);
        const text = risks.join(' ');
        if (text) {
          keyRisksCache.set(cacheKey, risks);
          setSummary(text);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return (
    <div className={standalone ? undefined : "border-t pt-4"}>
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
        <ShieldAlert className="h-3 w-3" />
        {t('detail.risks.keyRisks')}
        <span className="ml-1 text-[9px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-full normal-case tracking-normal">AI</span>
      </p>
      {loading && (
        <div className="space-y-1.5">
          <div className="h-2.5 bg-muted rounded animate-pulse w-full" />
          <div className="h-2.5 bg-muted rounded animate-pulse w-4/5" />
          <div className="h-2.5 bg-muted rounded animate-pulse w-3/5" />
        </div>
      )}
      {!loading && summary && (
        <p className="text-xs text-muted-foreground leading-relaxed">{summary}</p>
      )}
    </div>
  );
}
